import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { 
  Users, 
  UserMinus, 
  Search, 
  Calendar,
  Ticket,
  ExternalLink,
  Loader2,
  Heart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Following() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [unfollowingId, setUnfollowingId] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (!authLoading && isAuthenticated) {
      fetchFollowing();
    }
  }, [authLoading, isAuthenticated]);

  const fetchFollowing = async () => {
    try {
      const res = await axios.get(`${API_URL}/follow/my-following`);
      
      // Enrich with event counts
      const enriched = await Promise.all(
        res.data.map(async (creator) => {
          try {
            const eventsRes = await axios.get(`${API_URL}/creators/${creator.id}/events`);
            const upcomingEvents = eventsRes.data.filter(e => 
              e.status === 'published' && new Date(e.event_date) > new Date()
            );
            return {
              ...creator,
              upcoming_events: upcomingEvents.length,
              total_events: eventsRes.data.length
            };
          } catch {
            return { ...creator, upcoming_events: 0, total_events: 0 };
          }
        })
      );
      
      setFollowing(enriched);
    } catch (error) {
      console.error('Error fetching following:', error);
      toast.error('Failed to load followed creators');
    } finally {
      setLoading(false);
    }
  };

  const handleUnfollow = async (creatorId, creatorName) => {
    setUnfollowingId(creatorId);
    try {
      await axios.delete(`${API_URL}/follow/${creatorId}`);
      setFollowing(following.filter(c => c.id !== creatorId));
      toast.success(`Unfollowed ${creatorName}`);
    } catch (error) {
      toast.error('Failed to unfollow');
    } finally {
      setUnfollowingId(null);
    }
  };

  const filteredFollowing = following.filter(creator =>
    creator.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    creator.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Heart className="w-8 h-8 text-primary" />
            <h1 className="font-heading font-bold text-3xl">Following</h1>
          </div>
          <p className="text-muted-foreground">
            Creators you follow • Get notified about their new events
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search creators..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-following"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <Card className="bg-card border-white/10">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{following.length}</p>
                <p className="text-sm text-muted-foreground">Following</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-white/10">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {following.reduce((acc, c) => acc + (c.upcoming_events || 0), 0)}
                </p>
                <p className="text-sm text-muted-foreground">Upcoming Events</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Following List */}
        {filteredFollowing.length === 0 ? (
          <Card className="bg-card border-white/10">
            <CardContent className="py-12 text-center">
              {following.length === 0 ? (
                <>
                  <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="font-heading font-semibold text-lg mb-2">
                    Not following anyone yet
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Follow your favorite creators to get notified about their events
                  </p>
                  <Button onClick={() => navigate('/events')}>
                    <Search className="w-4 h-4 mr-2" />
                    Browse Events
                  </Button>
                </>
              ) : (
                <>
                  <Search className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="font-heading font-semibold text-lg mb-2">
                    No results found
                  </h3>
                  <p className="text-muted-foreground">
                    No creators match "{searchQuery}"
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredFollowing.map((creator) => (
              <Card 
                key={creator.id} 
                className="bg-card border-white/10 hover:border-primary/30 transition-colors"
                data-testid={`following-creator-${creator.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Avatar */}
                    <Avatar 
                      className="w-16 h-16 cursor-pointer"
                      onClick={() => navigate(`/creator/${creator.username || creator.id}`)}
                    >
                      <AvatarImage src={creator.avatar_url} />
                      <AvatarFallback className="bg-primary/20 text-primary text-xl">
                        {creator.display_name?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 
                          className="font-heading font-semibold truncate cursor-pointer hover:text-primary transition-colors"
                          onClick={() => navigate(`/creator/${creator.username || creator.id}`)}
                        >
                          {creator.display_name}
                        </h3>
                        {creator.verified && (
                          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                            Verified
                          </Badge>
                        )}
                      </div>
                      {creator.username && (
                        <p className="text-sm text-muted-foreground mb-2">
                          @{creator.username}
                        </p>
                      )}
                      <div className="flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          {creator.upcoming_events || 0} upcoming
                        </span>
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Ticket className="w-4 h-4" />
                          {creator.total_events || 0} events
                        </span>
                        {creator.follower_count > 0 && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Users className="w-4 h-4" />
                            {creator.follower_count.toLocaleString()} followers
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/creator/${creator.username || creator.id}`)}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleUnfollow(creator.id, creator.display_name)}
                        disabled={unfollowingId === creator.id}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        {unfollowingId === creator.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <UserMinus className="w-4 h-4 mr-1" />
                            Unfollow
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
